import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import {
  OBJECT_DETECTION_ICONS,
  AUDIO_DETECTION_ICONS,
  DEFAULT_OBJECT_ICON,
  DEFAULT_AUDIO_ICON,
} from '@/config/detectionIcons'
import { useDirectionStore } from '@/store/directionStore'

/**
 * Shared detection chip — used in EventsPage, EventDetailModal, and notification cards.
 * Single source of truth for detection class display.
 */
export function DetectionChip({
  cls,
  confidence,
  type,
}: {
  cls: string
  confidence?: number
  type: 'object' | 'audio'
}) {
  const { t } = useTranslation('common')
  const direction = useDirectionStore(s => s.direction)
  const config =
    type === 'object'
      ? (OBJECT_DETECTION_ICONS[cls] ?? DEFAULT_OBJECT_ICON)
      : (AUDIO_DETECTION_ICONS[cls] ?? DEFAULT_AUDIO_ICON)

  const nsKey = type === 'object' ? 'detectionClasses' : 'audioClasses'
  const label = t(`${nsKey}.${cls}`, { defaultValue: config.label || cls.replace(/_/g, ' ') })
  const confidencePct = confidence !== undefined ? Math.round(confidence * 100) : null

  const Icon = config.icon

  // Direction-aware chip shell overlay
  const chipOverlay =
    direction === 'command' ? 'font-mono uppercase tracking-wide' :
    direction === 'saas' ? 'bg-transparent border-border text-[11px]' :
    direction === 'expressive' ? 'bg-glass border-border/50' :
    ''

  // Direction-aware confidence text overlay
  // Note: saas overlay had text-[11px] tabular-nums but those are already in the base span — removed as duplicates.
  const confOverlay =
    direction === 'command' ? 'font-mono' :
    ''

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border cursor-default',
        'bg-muted border-border/60 h-7 px-2.5',
        'hover:bg-accent hover:border-border transition-colors duration-150',
        chipOverlay,
      )}
    >
      {direction === 'expressive' && (
        <span className="h-1.5 w-1.5 rounded-full grad-accent shrink-0" />
      )}
      <Icon className={cn('h-4 w-4 flex-shrink-0', config.color)} weight="duotone" />
      <span className="text-xs font-medium text-foreground/75 whitespace-nowrap leading-none">
        {label}
      </span>
      {confidencePct !== null && (
        <span className={cn('text-[11px] font-semibold text-foreground/55 tabular-nums leading-none', confOverlay)}>
          {direction === 'command' ? `·${confidencePct}%` : `${confidencePct}%`}
        </span>
      )}
    </span>
  )
}
